// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { StorageView } from '@Hooks/useStorageView';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/locale';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import useValidateForm from '@Hooks/useValidateForm';

import './ManageViewCreateDrawer.scss';

interface ManageViewCreateDrawerProps {
    views: StorageView;
    onChange?: (viewId: string) => void;
    onCreate?: (viewId: string) => void;
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ManageViewCreateDrawer = ({
    views,
    triggerElem,
    onCreate,
    onChange,
    visible,
    onVisibleChange,
}: ManageViewCreateDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const listView = Object.keys(views);

    return (
        <THDrawer
            title={i('view.drawer.title.create')}
            triggerElem={triggerElem}
            className="drawer-form"
            width="50vw"
            disableClose={fieldsTouched}
            visible={visible}
            afterVisibleChange={(visible) => {
                onVisibleChange?.(visible);
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    name="manege-view-create-form"
                    form={form}
                    onValuesChange={(obj) => {
                        if (obj.name) {
                            onChange?.(obj.name);
                        }
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={({ name }) => {
                        onCreate?.(name);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    toggleDrawer();
                                    setFieldsTouched(false);
                                }}
                                disabled={!isValid}
                                footerText={i('general.confirm')}
                            />
                        }
                    >
                        <THForm.Item
                            label={i('view.fields.name')}
                            name="name"
                            rules={[
                                { required: true, message: i('view.required-name') },
                                {
                                    message: i('view.message.already-exist'),
                                    validator: (_, value) => {
                                        if (listView && !listView.includes(value)) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject(i('view.message.already-exist'));
                                        }
                                    },
                                },
                            ]}
                        >
                            <THInput />
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
